import { getNetworkErrorMessage } from '@framework/ui/core/errors/getNetworkErrorMessage.tsx';
import { type yup } from '@framework/validation/initYup.ts';
import { isAxiosError } from 'axios';
import { isEqual } from 'lodash';
import { buildAsyncServiceParams } from '../../../../../expressions/repository/async/buildAsyncServiceParams.ts';
import { getEngineAsyncClient } from '../../../../../expressions/repository/async/client/getEngineAsyncClient.ts';
import { fetchAsyncService } from '../../../../../expressions/repository/async/fetch/fetchAsyncService.ts';
import { parseAsyncServiceUrl } from '../../../../../expressions/repository/async/url/parseAsyncServiceUrl.ts';
import { type IAsyncValueWidget } from '../IAsyncValueWidget.ts';
import { type IExternalServiceContext } from '../IExternalServiceContext.ts';

/**
 * Costruisce la funzione di test per la validazione del widget AsyncValue
 * nella quale viene richiamato il servizio esterno e confrontati i risultati.
 */
export const buildAsyncServiceValidationTestFn: (
  context: IExternalServiceContext,
  widget: IAsyncValueWidget,
  variables: Record<string, any>,
  objectSchema: yup.ObjectSchema<yup.AnyObject>,
) => yup.TestFunction<any, yup.AnyObject> = (
  externalServiceContext,
  widget,
  variables,
  objectSchema,
) =>
  async function (value) {
    const { externalServices, apiClient } = externalServiceContext;

    // Ricerco la configurazione del servizio chiamato dal widget
    const service = externalServices.find(s => s.id === widget.serviceId);

    // Se non esiste il servizio, errore
    if (!service) {
      return this.createError({
        path: this.path,
        message: `Nessun servizio esterno trovato per id ${widget.serviceId}`,
      });
    }

    const { originalParams } = parseAsyncServiceUrl(service);

    const params = buildAsyncServiceParams(service, widget.inputs, variables);

    // Recupero il client HTTP da usare per la chiamata (uso la versione autenticata solo per
    // le chiamate ai microservizi interni, per i servizi esterni uso un client senza autenticazione)
    const httpClient = getEngineAsyncClient({
      isExternal: service.isExternal,
      apiClient,
    });

    try {
      // Richiamo il servizio esterno per recuperare il dato
      const data = await fetchAsyncService({
        service,
        url: service.url,
        originalParams,
        httpClient,
        params,
      });

      // Ci assicuriamo di avere un oggetto (primo elemento dell'array se
      // il risultato è un array)
      const result = Array.isArray(data) ? data[0] : data;
      // Casto il risultato in base allo schema dell'oggetto (in questo modo ho un oggetto
      // con le stesse chiavi e tipi di quello salvato)
      const castedResult = objectSchema.cast(result, { assert: false });

      return isEqual(castedResult, value);
    } catch (error) {
      // Non gestiamo l'errore qui, lo logghiamo e rilanciamo, lasciando che
      // venga gestito dal chiamante (e.g. validator API)
      if (isAxiosError(error)) {
        // biome-ignore format: error management
        const errorMessage = getNetworkErrorMessage( error, 'Errore di rete sconosciuto');
        // biome-ignore format: error management
        console.error( `Errore durante la chiamata al servizio esterno ${service.id} (${service.url}): ${errorMessage}\nDettagli errore Axios: ${error.response?.status} - ${error.response?.statusText} - Request: ${error.request?.method} ${error.request?.path}`, { data: error.response?.data });
      }
      throw error;
    }
  };
